import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Menu,
  X,
  User,
  LogOut,
  LayoutGrid,
  ChevronDown,
} from 'lucide-react';
import toast from 'react-hot-toast';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import LocalizedLink from '../i18n/LocalizedLink';
import { logout } from '../../features/auth/authSlice';
import { DEFAULT_LANGUAGE } from '../../i18n';
import JourneyButton from '../ui/JourneyButton.jsx';
import { resolveBrandSettings } from '../../features/branding/brandSettings';
import resolveImageUrl from '../../utils/resolveImageUrl';
const FALLBACK_NAV = [
  { _id: 'home', label: 'Home', href: '/', isExternal: false },
  { _id: 'packages', label: 'Packages', href: '/packages', isExternal: false },
  { _id: 'destinations', label: 'Destinations', href: '/destinations', isExternal: false },
  { _id: 'about', label: 'About Us', href: '/about', isExternal: false },
];

const LEGACY_NAV_URL_MAP = {
  '/products': '/packages',
  '/products/': '/packages',
  '/get-quote': '/packages/inquiry',
  '/inquiry': '/packages/inquiry',
  '/bookings/new': '/packages/inquiry',
  '/mvp': '/',
  '/mvp/': '/',
  '/mvp/packages': '/packages',
  '/mvp/destinations': '/destinations',
  '/mvp/blog': '/blog',
};

const normalizeNavHref = (href = '') => {
  const trimmed = String(href || '').trim();
  if (!trimmed) return '/';
  return LEGACY_NAV_URL_MAP[trimmed] || trimmed;
};

const buildNavTree = (items = []) => {
  const nodes = items.map((item) => ({ ...item, children: [] }));
  const byId = new Map(nodes.map((item) => [String(item._id), item]));
  const roots = [];

  nodes.forEach((node) => {
    const parentId = node.parent ? String(node.parent) : '';
    const parentNode = parentId ? byId.get(parentId) : null;

    if (parentNode) {
      parentNode.children.push(node);
      return;
    }

    roots.push(node);
  });

  const sortTree = (list) => {
    list.sort(
      (a, b) =>
        (a.order ?? 0) - (b.order ?? 0) ||
        String(a.label || '').localeCompare(String(b.label || ''))
    );
    list.forEach((node) => {
      if (node.children.length > 0) {
        sortTree(node.children);
      }
    });
    return list;
  };

  return sortTree(roots);
};

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountHover, setAccountHover] = useState(false);
  const [desktopMenuOpenId, setDesktopMenuOpenId] = useState(null);
  const [mobileMenuOpenIds, setMobileMenuOpenIds] = useState({});

  const hoverTimeoutRef = useRef(null);
  const desktopMenuCloseTimeoutRef = useRef(null);
  const accountButtonRef = useRef(null);
  const dashboardItemRef = useRef(null);
  const adminItemRef = useRef(null);
  const logoutItemRef = useRef(null);
  const prevAccountOpenRef = useRef(accountOpen);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { lang } = useParams();

  const currentLang = lang || DEFAULT_LANGUAGE;
  const normalizedPathname = location.pathname.replace(/\/+$/, '') || '/';
  const isHomeRoute =
    normalizedPathname === '/' ||
    normalizedPathname === `/${currentLang}`;
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { items: navItems } = useSelector((state) => state.nav);
  const brand = resolveBrandSettings(useSelector((state) => state.branding.settings));
  const headerLogoUrl = resolveImageUrl(brand.logo.primary.url);
  const navSource = navItems && navItems.length > 0 ? navItems : FALLBACK_NAV;
  const seenInternalHrefs = new Set();
  const navLinks = navSource
    .map((item) => (
      item.isExternal
        ? item
        : { ...item, href: normalizeNavHref(item.href) }
    ))
    .filter((item) => {
      if (item.isExternal) return true;
      if (seenInternalHrefs.has(item.href)) return false;
      seenInternalHrefs.add(item.href);
      return true;
    });
  const navTree = buildNavTree(navLinks);

  const showAccountMenu = accountOpen || accountHover;
  const hasAdminAccess = ['admin', 'super_admin', 'developer'].includes(user?.role);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (desktopMenuCloseTimeoutRef.current) clearTimeout(desktopMenuCloseTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) {
      setDesktopMenuOpenId(null);
      setMobileMenuOpenIds({});
    }
  }, [menuOpen]);

  const openDesktopMenu = (menuId) => {
    if (desktopMenuCloseTimeoutRef.current) clearTimeout(desktopMenuCloseTimeoutRef.current);
    setDesktopMenuOpenId(menuId);
  };

  const closeDesktopMenuWithDelay = (menuId) => {
    if (desktopMenuCloseTimeoutRef.current) clearTimeout(desktopMenuCloseTimeoutRef.current);
    desktopMenuCloseTimeoutRef.current = setTimeout(() => {
      setDesktopMenuOpenId((current) => (current === menuId ? null : current));
    }, 170);
  };

  useEffect(() => {
    const wasOpen = prevAccountOpenRef.current;

    if (accountOpen && !wasOpen) {
      const frame = requestAnimationFrame(() => {
        dashboardItemRef.current?.focus();
      });
      prevAccountOpenRef.current = accountOpen;
      return () => cancelAnimationFrame(frame);
    }

    if (!accountOpen && wasOpen) {
      accountButtonRef.current?.focus();
    }

    prevAccountOpenRef.current = accountOpen;
  }, [accountOpen]);

  const handleLogout = () => {
    dispatch(logout());
    toast.success('Logged out successfully');
    navigate(`/${currentLang}`);
    setMenuOpen(false);
    setAccountOpen(false);
    setAccountHover(false);
  };

  const handleAccountMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setAccountHover(true);
  };

  const handleAccountMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setAccountHover(false);
    }, 140);
  };

  const getAccountMenuItems = () => {
    const items = [dashboardItemRef];

    if (hasAdminAccess) {
      items.push(adminItemRef);
    }

    items.push(logoutItemRef);

    return items
      .map((ref) => ref.current)
      .filter(Boolean);
  };

  const handleAccountMenuKeyDown = (event) => {
    if (!showAccountMenu) return;

    const items = getAccountMenuItems();
    if (!items.length) return;

    const currentIndex = items.findIndex((item) => item === document.activeElement);

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % items.length : 0;
        items[nextIndex]?.focus();
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        const prevIndex =
          currentIndex >= 0 ? (currentIndex - 1 + items.length) % items.length : items.length - 1;
        items[prevIndex]?.focus();
        break;
      }
      case 'Home': {
        event.preventDefault();
        items[0]?.focus();
        break;
      }
      case 'End': {
        event.preventDefault();
        items[items.length - 1]?.focus();
        break;
      }
      case 'Escape': {
        event.preventDefault();
        setAccountOpen(false);
        setAccountHover(false);
        accountButtonRef.current?.focus();
        break;
      }
      default:
        break;
    }
  };

  const desktopNavLink =
    'group relative type-nav transition-colors duration-200 hover:text-primary-900';

  const mobileNavLink =
    'block rounded-xl px-4 py-3 type-menu transition hover:bg-primary-50 hover:text-primary-900';
  const mobileParentLink =
    'flex-1 rounded-xl py-3 type-menu transition hover:bg-transparent';

  const accountMenuItem =
    'flex items-center rounded-xl px-3 py-3 type-menu transition hover:bg-pearl-100 hover:text-primary-900 focus:outline-none focus:ring-2 focus:ring-gold-400/40';

  const closeAllMenus = () => {
    if (desktopMenuCloseTimeoutRef.current) clearTimeout(desktopMenuCloseTimeoutRef.current);
    setMenuOpen(false);
    setDesktopMenuOpenId(null);
    setMobileMenuOpenIds({});
  };

  const renderNavLink = (item, className, onClick, labelClassName = '') => {
    const isHomeItem = item._id === 'home' && isHomeRoute;
    const linkClassName = isHomeItem
      ? `${className} cursor-default opacity-55`
      : className;
    const label = (
      <span className={`relative inline-flex items-center gap-2 ${labelClassName}`}>
        <span>{item.label}</span>
        {!isHomeItem && (
          <span className="absolute -bottom-2 left-0 h-px w-0 bg-gold-500 transition-all duration-300 group-hover:w-full" />
        )}
      </span>
    );

    if (isHomeItem) {
      return (
        <span
          className={linkClassName}
          aria-current="page"
          aria-disabled="true"
        >
          {label}
        </span>
      );
    }

    if (item.isExternal) {
      return (
        <a
          href={item.href}
          target={item.openInNewTab ? '_blank' : '_self'}
          rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
          className={linkClassName}
          onClick={onClick}
        >
          {label}
        </a>
      );
    }

    return (
      <LocalizedLink to={item.href} className={linkClassName} onClick={onClick}>
        {label}
      </LocalizedLink>
    );
  };

  const renderMobileNavNode = (item, depth = 0) => {
    const children = item.children || [];
    const hasChildren = children.length > 0;
    const isExpanded = Boolean(mobileMenuOpenIds[item._id]);
    const isRoot = depth === 0;
    const linkClass = isRoot
      ? mobileNavLink
      : `${mobileNavLink} ml-4 border-l border-pearl-300 pl-6 text-sm text-primary-700`;

    if (!hasChildren) {
      return renderNavLink(item, linkClass, closeAllMenus);
    }

    return (
      <div key={item._id} className={isRoot ? 'rounded-xl border border-pearl-200 bg-white' : ''}>
        <div className="flex items-center gap-2 rounded-xl px-4 py-3">
          {renderNavLink(item, mobileParentLink, closeAllMenus)}

          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-pearl-300 text-primary-900 transition hover:border-gold-400 hover:text-gold-600"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setMobileMenuOpenIds((current) => ({
                ...current,
                [item._id]: !current[item._id],
              }));
            }}
            aria-label={`Toggle ${item.label} submenu`}
            aria-expanded={isExpanded}
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
              strokeWidth={1.9}
            />
          </button>
        </div>

        {isExpanded && (
          <div className="space-y-1 border-t border-pearl-300/80 bg-pearl-50 px-2 py-2">
            {children.map((child) => (
              <div key={child._id}>{renderMobileNavNode(child, depth + 1)}</div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDesktopNavNode = (item) => {
    const children = item.children || [];
    const hasChildren = children.length > 0;
    const isOpen = desktopMenuOpenId === item._id;

    if (!hasChildren) {
      return renderNavLink(item, desktopNavLink, () => setDesktopMenuOpenId(null));
    }

    return (
      <div
        key={item._id}
        className="relative"
        onMouseEnter={() => openDesktopMenu(item._id)}
        onMouseLeave={() => closeDesktopMenuWithDelay(item._id)}
        onFocusCapture={() => openDesktopMenu(item._id)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setDesktopMenuOpenId((current) => (current === item._id ? null : current));
          }
        }}
      >
        <div className="flex items-center gap-2">
          {renderNavLink(item, desktopNavLink, () => setDesktopMenuOpenId(null))}

          <button
            type="button"
            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-transparent text-primary-700 transition hover:border-pearl-300 hover:text-primary-900"
            aria-haspopup="menu"
            aria-expanded={isOpen}
            aria-label={`Open ${item.label} submenu`}
            onClick={() => {
              if (desktopMenuCloseTimeoutRef.current) clearTimeout(desktopMenuCloseTimeoutRef.current);
              setDesktopMenuOpenId((current) => (current === item._id ? null : item._id));
            }}
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
              strokeWidth={1.9}
            />
          </button>
        </div>

        {isOpen && (
          <div className="absolute left-0 top-full z-50 pt-3">
            <div className="min-w-72 rounded-3xl border border-pearl-300 bg-white p-3 shadow-elevated">
              <div className="space-y-1">
                {children.map((child) => (
                  <div key={child._id} className="rounded-2xl">
                    {renderNavLink(
                      child,
                      'block rounded-2xl px-4 py-3 type-menu transition hover:bg-primary-50 hover:text-primary-900',
                      () => setDesktopMenuOpenId(null),
                      'text-sm'
                    )}
                    {child.children?.length > 0 && (
                      <div className="mt-1 space-y-1 border-l border-pearl-200 pl-3">
                        {child.children.map((grandchild) => (
                          <div key={grandchild._id}>
                            {renderNavLink(
                              grandchild,
                              'block rounded-xl px-4 py-2.5 text-sm type-menu transition hover:bg-primary-50 hover:text-primary-900',
                              () => setDesktopMenuOpenId(null)
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <header className="sticky top-0 z-50 border-b border-pearl-300/90 bg-white/90 backdrop-blur-2xl">
      <div className="container-shell min-[1536px]:max-w-[1680px] min-[1920px]:max-w-[1840px]">
        <div className="flex h-24 items-center justify-between lg:h-20">
          {/* Logo */}
          <LocalizedLink
            to="/"
            className="flex w-40 shrink-0 items-center overflow-hidden sm:w-64 md:w-72 lg:w-auto lg:overflow-visible"
            onClick={closeAllMenus}
          >
            <img
              src={headerLogoUrl}
              alt={brand.logo.altText}
              className="h-22 max-w-full object-contain md:h-[64px] lg:h-[64px] lg:max-w-none xl:h-[72px]"
            />
          </LocalizedLink>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
            {navTree.map((item) => (
              <React.Fragment key={item._id}>{renderDesktopNavNode(item)}</React.Fragment>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3 xl:gap-4 flex-nowrap">
            <div className="hidden xl:block">
              <LanguageSwitcher />
            </div>

            {isAuthenticated ? (
              <>
                {/* Account */}
                <div
                  className="relative"
                  onMouseEnter={handleAccountMouseEnter}
                  onMouseLeave={handleAccountMouseLeave}
                >
                  <button
                    ref={accountButtonRef}
                    type="button"
                    onClick={() => setAccountOpen((prev) => !prev)}
                    className={`inline-flex h-12 items-center justify-center gap-2 rounded-full border bg-white px-4 text-base font-medium tracking-subtle shadow-soft transition-all duration-200 ${
                      showAccountMenu
                        ? 'border-gold-400 text-primary-900'
                        : 'border-pearl-300 text-ink-700 hover:-translate-y-0.5 hover:border-gold-400 hover:text-primary-900'
                    }`}
                    aria-haspopup="menu"
                    aria-controls="account-menu"
                    aria-expanded={showAccountMenu}
                    aria-label="Account menu"
                  >
                    <User className="h-4.5 w-4.5" strokeWidth={1.9} />
                    <ChevronDown
                      className={`h-4 w-4 transition-transform duration-200 ${
                        showAccountMenu ? 'rotate-180' : 'rotate-0'
                      }`}
                      strokeWidth={1.9}
                    />
                  </button>

                  {showAccountMenu && (
                    <div
                      id="account-menu"
                      role="menu"
                      onKeyDown={handleAccountMenuKeyDown}
                      className="absolute right-0 top-full mt-3 w-64 rounded-2xl border border-pearl-300 bg-white p-2 shadow-elevated"
                    >
                      <div className="px-3 pb-2 pt-1">
                        <p className="text-base font-semibold tracking-subtle text-primary-900">
                          {user?.name || 'Account'}
                        </p>
                        <p className="type-support mt-1">
                          {user?.email || t('nav.manageAccount', 'Manage your profile and bookings')}
                        </p>
                      </div>

                      <div className="my-2 h-px bg-pearl-300" />

                      <LocalizedLink
                        ref={dashboardItemRef}
                        to="/dashboard"
                        role="menuitem"
                        className={accountMenuItem}
                        onClick={() => {
                          setAccountOpen(false);
                          setAccountHover(false);
                        }}
                      >
                        <LayoutGrid className="mr-3 h-4 w-4 text-gold-500" strokeWidth={1.9} />
                        {t('nav.dashboard', 'Dashboard')}
                      </LocalizedLink>

                      {hasAdminAccess && (
                        <Link
                          ref={adminItemRef}
                          to="/admin"
                          role="menuitem"
                          className={accountMenuItem}
                          onClick={() => {
                            setAccountOpen(false);
                            setAccountHover(false);
                          }}
                        >
                          <LayoutGrid className="mr-3 h-4 w-4 text-gold-500" strokeWidth={1.9} />
                          {t('nav.admin', 'Admin')}
                        </Link>
                      )}

                      <div className="my-2 h-px bg-pearl-300" />

                      <button
                        ref={logoutItemRef}
                        type="button"
                        onClick={handleLogout}
                        role="menuitem"
                        className="flex w-full items-center rounded-xl px-3 py-3 type-menu text-danger-500 transition hover:bg-danger-50"
                      >
                        <LogOut className="mr-3 h-4 w-4" strokeWidth={1.9} />
                        {t('nav.logout', 'Logout')}
                      </button>
                    </div>
                  )}
                </div>

                {/* Top CTA */}
                <JourneyButton as={LocalizedLink} to="/packages/inquiry">
                  {t('nav.planJourney', 'Plan Your Journey')}
                </JourneyButton>
              </>
            ) : (
              <div className="flex items-center gap-3 flex-nowrap">
                <LocalizedLink
                  to="/login"
                  className="type-nav transition hover:text-primary-900"
                >
                  {t('nav.signin', 'Sign in')}
                </LocalizedLink>

                <JourneyButton as={LocalizedLink} to="/packages/inquiry">
                  {t('nav.planJourney', 'Plan Your Journey')}
                </JourneyButton>
              </div>
            )}
          </div>

          {/* Mobile Top Actions */}
          <div className="flex shrink-0 items-center gap-2 lg:hidden flex-nowrap">
            {isAuthenticated ? (
              <JourneyButton
                as={LocalizedLink}
                to="/packages/inquiry"
                className="h-10 gap-1 px-2 py-2 text-xs sm:h-auto sm:gap-3 sm:px-4 sm:py-2.5 sm:text-base"
                onClick={closeAllMenus}
              >
                {t('nav.planJourney', 'Plan Your Journey')}
              </JourneyButton>
            ) : (
              <JourneyButton
                as={LocalizedLink}
                to="/packages/inquiry"
                className="h-10 gap-1 px-2 py-2 text-xs sm:h-auto sm:gap-3 sm:px-4 sm:py-2.5 sm:text-base"
                onClick={closeAllMenus}
              >
                {t('nav.planJourney', 'Plan Your Journey')}
              </JourneyButton>
            )}

            <button
              type="button"
              className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-pearl-300 bg-white text-primary-900 shadow-soft transition hover:border-gold-400 hover:text-gold-600"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <X className="h-5 w-5" strokeWidth={1.9} /> : <Menu className="h-5 w-5" strokeWidth={1.9} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="max-h-[calc(100vh-6rem)] overflow-y-auto border-t border-pearl-300 pb-6 pt-4 lg:hidden">
            <div className="mb-4 px-1">
              <LanguageSwitcher />
            </div>

            <nav className="flex flex-col gap-1">
              {navTree.map((item) => (
                <div key={item._id}>{renderMobileNavNode(item)}</div>
              ))}

              <div className="my-3 h-px bg-pearl-300" />

              {isAuthenticated ? (
                <>
                  <LocalizedLink
                    to="/dashboard"
                    className={mobileNavLink}
                    onClick={closeAllMenus}
                  >
                    {t('nav.dashboard', 'Dashboard')}
                  </LocalizedLink>

                  {['admin', 'super_admin', 'developer'].includes(user?.role) && (
                    <Link
                      to="/admin"
                      className={mobileNavLink}
                      onClick={closeAllMenus}
                    >
                      {t('nav.admin', 'Admin')}
                    </Link>
                  )}

                  <button
                    type="button"
                    onClick={handleLogout}
                    className="block rounded-xl px-4 py-3 text-left type-menu text-danger-500 transition hover:bg-danger-50"
                  >
                    {t('nav.logout', 'Logout')}
                  </button>

                  <div className="pt-3">
                    <JourneyButton
                      as={LocalizedLink}
                      to="/packages/inquiry"
                      className="w-full"
                      onClick={closeAllMenus}
                    >
                      {t('nav.planJourney', 'Plan Your Journey')}
                    </JourneyButton>
                  </div>
                </>
              ) : (
                <div className="flex flex-col gap-3 px-1 pt-2">
                  <LocalizedLink
                    to="/login"
                    className="inline-flex w-full items-center justify-center rounded-xl border border-primary-200 bg-pearl-100 px-5 py-3 type-menu text-primary-900 transition hover:bg-pearl-200"
                    onClick={closeAllMenus}
                  >
                    {t('nav.signin', 'Sign in')}
                  </LocalizedLink>

                  <JourneyButton
                    as={LocalizedLink}
                    to="/packages/inquiry"
                    className="w-full"
                    onClick={closeAllMenus}
                  >
                    {t('nav.planJourney', 'Plan Your Journey')}
                  </JourneyButton>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
